/**
 * Centralized layout constants with explanations.
 */

/**
 * Layout spacing and sizing constants.
 */
const LAYOUT_CONSTANTS = {
  /**
   * Vertical padding for panel content (pixels).
   * Applied to top and bottom of the media panel.
   */
  TOP_AND_BOTTOM_PADDING: 25,

  /**
   * Horizontal padding for panel content (pixels).
   * Applied to left and right of the media panel.
   */
  LEFT_AND_RIGHT_PADDING: 24,

  /**
   * Empty space between columns (pixels).
   * The default value for this is 1em which seems to be equivalent
   * to ~4px based on our current typography configuration.
   * Bumping this above 4px causes a horizontal scrollbar to appear.
   */
  SPACE_BETWEEN_COLUMNS: 6,

  /**
   * Empty space between rows (pixels).
   * This value is used both in the fullscreen and workspace views.
   * TODO: split this to provide different or relative values.
   */
  SPACE_BETWEEN_ROWS: 12,

  /**
   * Minimum width per column when column width is limited (e.g. compare fullscreen).
   * Must match the min value in getGridTemplateColumnsWithHeaders (minmax(188px, 1fr)).
   */
  MIN_COLUMN_WIDTH: 188,

  /**
   * Width of the row header column in grid mode (pixels).
   * This is the space reserved for vertically-oriented row labels.
   */
  ROW_HEADER_WIDTH: 24,

  /**
   * Line height for media captions (pixels).
   * Used to calculate available height when captions are shown.
   */
  CAPTION_LINE_HEIGHT: 22,

  /**
   * Maximum plotly charts per page before pagination.
   *
   * Plotly performs poorly with many charts. This limit
   * allows 2 panels to be open simultaneously with good
   * performance.
   */
  MAX_GALLERY_PLOTLY: 4,

  /** Max grid rows shown before the content area scrolls (floors each row at panel / N). */
  MAX_VISIBLE_GRID_ROWS: 3,

  /**
   * Minimum column count allowed.
   */
  MIN_COL_COUNT: 1,

  /**
   * Maximum column count allowed.
   * Prevents layouts from becoming unusably small.
   */
  MAX_COL_COUNT: 20,

  /**
   * Gap between content footer controls in gallery mode.
   */
  CONTENT_FOOTER_GAP_GALLERY: 16,

  /**
   * Gap between content footer controls in compare mode.
   */
  CONTENT_FOOTER_GAP_COMPARE: 8,
} as const;
type LayoutConstants = typeof LAYOUT_CONSTANTS;

/**
 * Default dimensions for different media types (pixels).
 */
const MEDIA_DIMENSIONS = {
  /** Default height for audio players */
  AUDIO_HEIGHT: 100,

  /** Default card width fallback */
  DEFAULT_CARD_WIDTH: 300,

  /** Default card height fallback */
  DEFAULT_CARD_HEIGHT: 300,
} as const;
type MediaDimensions = typeof MEDIA_DIMENSIONS;

/**
 * Performance thresholds.
 */
const PERFORMANCE_THRESHOLDS = {
  /** Max tiles to render without pagination (gallery images) */
  MAX_IMAGES_WITHOUT_PAGINATION: 32,

  /**
   * Max tiles to render without pagination (gallery video). Lower than images
   * (decode/playback cost); same numeric cap as compare fan-out (MAX_COMPARE_ROWS_WITHOUT_PAGINATION).
   */
  MAX_VIDEOS_WITHOUT_PAGINATION: 20,

  /** Max tiles to render without pagination (plotly) */
  MAX_PLOTLY_WITHOUT_PAGINATION: 4,

  /** Max rows to render without pagination in compare mode with fan-out */
  MAX_COMPARE_ROWS_WITHOUT_PAGINATION: 20,
} as const;
type PerformanceThreshold = typeof PERFORMANCE_THRESHOLDS;

export {LAYOUT_CONSTANTS, type LayoutConstants};
export {MEDIA_DIMENSIONS, type MediaDimensions};
export {PERFORMANCE_THRESHOLDS, type PerformanceThreshold};
